import React, { useMemo, useState } from 'react';
import { ProgressBar, Spinner, TagColored, Tabs, Text } from '@spark';
import DiffSection from './DiffSection';
import './versionsDiff.css';

const TABS = [
  { id: 'counts', label: 'Total Count & Distribution %' },
  { id: 'review', label: 'Review %' },
];

const round1 = (value) => Math.round(value * 10) / 10;

/** Trend pill for the DIFF column: lime for positive, volcano for negative, "-" for none. */
const DiffPill = ({ value, newLabel = false }) => {
  if (newLabel) {
    return <TagColored variant="lime" label="New" showPrefixIcon prefixIconSlug="plus" />;
  }
  if (value == null || value === 0) {
    return <Text variant="base-regular" color="secondary">-</Text>;
  }
  const positive = value > 0;
  return (
    <TagColored
      variant={positive ? 'lime' : 'volcano'}
      label={`${positive ? '+' : ''}${value}%`}
      showPrefixIcon
      prefixIconSlug={positive ? 'trending-up' : 'trending-down'}
    />
  );
};

const reviewPct = (entry) =>
  entry && entry.total > 0 ? Math.round((entry.validated / entry.total) * 100) : 0;

/**
 * "Label distribution and % reviewed diff" collapsible section ("Entity
 * distribution ..." on NER tasks): per-label counts / review progress for
 * both versions with a trend DIFF column (Figma nodes 5208:32988 / 5222:13514).
 */
const LabelDistributionSection = ({ distribution, fromLabel, toLabel, loading = false }) => {
  const [activeTab, setActiveTab] = useState('counts');
  const noun = distribution?.kind === 'entities' ? 'Entity' : 'Label';

  const rows = useMemo(() => {
    const fromMap = distribution?.from || {};
    const toMap = distribution?.to || {};
    const labels = [...new Set([...Object.keys(fromMap), ...Object.keys(toMap)])];
    const empty = { total: 0, validated: 0 };
    return labels
      .map((label) => ({ label, from: fromMap[label] || empty, to: toMap[label] || empty }))
      .sort((a, b) => b.to.total - a.to.total || a.label.localeCompare(b.label));
  }, [distribution]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, row) => ({
          from: {
            total: acc.from.total + row.from.total,
            validated: acc.from.validated + row.from.validated,
          },
          to: {
            total: acc.to.total + row.to.total,
            validated: acc.to.validated + row.to.validated,
          },
        }),
        { from: { total: 0, validated: 0 }, to: { total: 0, validated: 0 } },
      ),
    [rows],
  );

  const countsDiff = (from, to) => {
    if (from.total === to.total) return { value: 0 };
    if (from.total === 0) return { newLabel: true };
    return { value: round1(((to.total - from.total) / from.total) * 100) };
  };

  const reviewDiff = (from, to) => ({ value: round1(reviewPct(to) - reviewPct(from)) });

  const renderCountsCell = (entry, versionTotal) => {
    const share = versionTotal > 0 ? round1((entry.total / versionTotal) * 100) : null;
    return (
      <Text variant="base-regular" color="primary">
        {entry.total.toLocaleString()}
        {share != null ? (
          <Text variant="base-regular" color="secondary">{` (${share}%)`}</Text>
        ) : null}
      </Text>
    );
  };

  const renderReviewCell = (entry) => (
    <div className="vdm-review-cell">
      <Text variant="base-regular" color="primary">
        {entry.validated.toLocaleString()}/{entry.total.toLocaleString()}
      </Text>
      <ProgressBar value={reviewPct(entry)} showPercentage />
    </div>
  );

  return (
    <DiffSection title={`${noun} distribution and % reviewed diff`} defaultOpen>
      {loading ? (
        <div className="vdm-loading"><Spinner size="small" /></div>
      ) : rows.length === 0 ? (
        <Text variant="base-regular" color="secondary">{`No ${noun.toLowerCase()} data available`}</Text>
      ) : (
        <>
          <Tabs
            items={TABS}
            activeId={activeTab}
            onChange={setActiveTab}
            size="medium"
            className="vdm-distribution-tabs"
          />
          <div className="vdm-table vdm-distribution-table" role="table">
            <div className="vdm-table-header-row" role="row">
              <span role="columnheader" />
              <Text variant="uniq-semibold-table-caps" color="secondary" role="columnheader">
                {fromLabel}
              </Text>
              <Text variant="uniq-semibold-table-caps" color="secondary" role="columnheader">
                {toLabel}
              </Text>
              <Text variant="uniq-semibold-table-caps" color="secondary" role="columnheader">
                Diff
              </Text>
            </div>
            {rows.map((row) => (
              <div className="vdm-table-row" role="row" key={row.label}>
                <Text variant="base-regular" color="primary" role="cell">{row.label}</Text>
                <span role="cell">
                  {activeTab === 'counts'
                    ? renderCountsCell(row.from, totals.from.total)
                    : renderReviewCell(row.from)}
                </span>
                <span role="cell">
                  {activeTab === 'counts'
                    ? renderCountsCell(row.to, totals.to.total)
                    : renderReviewCell(row.to)}
                </span>
                <span role="cell">
                  <DiffPill
                    {...(activeTab === 'counts'
                      ? countsDiff(row.from, row.to)
                      : reviewDiff(row.from, row.to))}
                  />
                </span>
              </div>
            ))}
            <div className="vdm-table-row vdm-table-row--total" role="row">
              <Text variant="base-semibold" color="primary" role="cell">All</Text>
              <span role="cell">
                {activeTab === 'counts' ? (
                  <Text variant="base-semibold" color="primary">
                    {totals.from.total.toLocaleString()}
                    <Text variant="base-regular" color="secondary"> (100%)</Text>
                  </Text>
                ) : (
                  renderReviewCell(totals.from)
                )}
              </span>
              <span role="cell">
                {activeTab === 'counts' ? (
                  <Text variant="base-semibold" color="primary">
                    {totals.to.total.toLocaleString()}
                    <Text variant="base-regular" color="secondary"> (100%)</Text>
                  </Text>
                ) : (
                  renderReviewCell(totals.to)
                )}
              </span>
              <span role="cell">
                <DiffPill
                  {...(activeTab === 'counts'
                    ? countsDiff(totals.from, totals.to)
                    : reviewDiff(totals.from, totals.to))}
                />
              </span>
            </div>
          </div>
        </>
      )}
    </DiffSection>
  );
};

export default LabelDistributionSection;
